import type * as React from 'react'

import { clsx } from 'clsx'

import {
  Flex,
  Heading,
  Text,
  useIsMobileScreenSize,
} from '@buffer-mono/popcorn'
import type { FlexProps } from '@buffer-mono/popcorn'

import { usePageTitle } from '~publish/hooks/usePageTitle'

import style from './SettingsPageLayout.module.css'

import type { JSX } from 'react'

type NewSettingsPageLayoutProps = {
  children: React.ReactNode
  title: React.ReactNode
  pageTitle?: string
  subtitle?: string | React.ReactNode
  wide?: boolean
  actions?: React.ReactNode
  /**
   * Keep `actions` on the same row as the title on mobile instead of stacking
   * them below. Meant for pages with a single CTA (e.g. "Invite Member");
   * pages with several actions should keep the stacked default so the buttons
   * don't fight the title for one row.
   */
  inlineActionsOnMobile?: boolean
  /**
   * Vertical alignment of `actions` within the header. Defaults to `end` so
   * a single button lines up with the subtitle; `start` lines it up with the
   * title instead.
   */
  actionsAlign?: 'start' | 'end'
  gap?: FlexProps['gap']
  testId?: string
  className?: string
}

const SettingsPageRoot = ({
  children,
  pageTitle = '',
  wide = false,
  testId,
  className,
}: Pick<
  NewSettingsPageLayoutProps,
  'children' | 'pageTitle' | 'wide' | 'testId' | 'className'
>): JSX.Element => {
  const resolvedTestId =
    testId ??
    (pageTitle ? `${pageTitle.toLowerCase()}-settings-page` : 'settings-page')

  usePageTitle(pageTitle)

  return (
    <div className={style.wrapper} data-testid={resolvedTestId}>
      <div className={clsx(wide ? style.innerWide : style.inner, className)}>
        <Flex direction="column" gap="xl" className={style.innerContent}>
          {children}
        </Flex>
      </div>
    </div>
  )
}

const SettingsPageTitle = ({
  children,
}: Pick<NewSettingsPageLayoutProps, 'children'>): JSX.Element => (
  <Heading as="h1" size="large" className={style.heading}>
    <Flex align="center" fullWidth>
      <Flex direction="column" gap="2xs" fullWidth>
        {children}
      </Flex>
    </Flex>
  </Heading>
)

const SettingsPageContent = ({
  children,
  gap = 'xs',
}: Pick<NewSettingsPageLayoutProps, 'children' | 'gap'>): JSX.Element => (
  <Flex
    direction="column"
    gap={gap}
    fullWidth
    style={{ paddingBottom: 'var(--space-2xl)' }}
  >
    {children}
  </Flex>
)

const SettingsPageLayout = ({
  children,
  title,
  pageTitle,
  subtitle,
  wide = false,
  actions = null,
  inlineActionsOnMobile = false,
  actionsAlign = 'end',
  gap = 'xs',
  testId,
  className,
}: NewSettingsPageLayoutProps): JSX.Element => {
  const documentTitle =
    pageTitle ??
    (typeof title === 'string' && title ? `Settings - ${title}` : '')
  const isMobile = useIsMobileScreenSize()
  // Single-CTA pages keep the action beside the title on mobile; the stacked
  // default remains for pages whose actions wouldn't fit on the title row.
  const inlineHeader = !isMobile || inlineActionsOnMobile

  return (
    <SettingsPageRoot
      pageTitle={documentTitle}
      wide={wide}
      testId={testId}
      className={className}
    >
      <Flex
        as="header"
        gap="sm"
        direction={inlineHeader ? 'row' : 'column'}
        align="start"
        justify={inlineHeader ? 'between' : 'start'}
        fullWidth
      >
        <SettingsPageTitle>
          {title}
          {subtitle && !isMobile && (
            <Text as="div" color="subtle">
              {subtitle}
            </Text>
          )}
        </SettingsPageTitle>
        <Flex
          align="center"
          gap="sm"
          className={clsx(
            style.actions,
            isMobile && inlineActionsOnMobile
              ? style.actionsInline
              : actionsAlign === 'start' && style.actionsStart,
          )}
        >
          {actions}
        </Flex>
      </Flex>
      <SettingsPageContent gap={gap}>{children}</SettingsPageContent>
    </SettingsPageRoot>
  )
}

export const NewSettingsPageLayout = Object.assign(SettingsPageLayout, {
  Root: SettingsPageRoot,
  Title: SettingsPageTitle,
  Content: SettingsPageContent,
})
